
import { useEffect } from "react"
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog"
import { LetterEditor } from "./LetterEditor"
import { PaymentForm } from "./payment/PaymentForm"
import { DiscountCodeInput } from "./payment/DiscountCodeInput"
import { PaymentDeliverySelector } from "./payment/PaymentDeliverySelector"
import { PaymentSummary } from "./payment/PaymentSummary"
import { usePayment } from "@/hooks/usePayment"
import { deliveryOptions } from "@/data/deliveryOptions"

interface PaymentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  formData: any
  initialDeliveryMethod?: string | null
}

export function PaymentDialog({ open, onOpenChange, formData, initialDeliveryMethod }: PaymentDialogProps) {
  const {
    selectedOption,
    clientSecret,
    processing,
    showLetterEditor,
    discountCode,
    currentAmount,
    setDiscountCode,
    handleOptionSelect,
    applyDiscountCode,
    handlePaymentSuccess,
    setShowLetterEditor
  } = usePayment({ initialDeliveryMethod })

  // Initialize with initial delivery method if provided
  useEffect(() => {
    if (initialDeliveryMethod) {
      handleOptionSelect(initialDeliveryMethod, deliveryOptions)
    }
  }, [initialDeliveryMethod])

  if (showLetterEditor) {
    return <LetterEditor 
      open={true} 
      onOpenChange={(open) => {
        setShowLetterEditor(open)
        if (!open) {
          onOpenChange(false)
        }
      }} 
      formData={formData} 
    />
  }

  const selectedDeliveryOption = deliveryOptions.find(opt => opt.id === selectedOption)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <PaymentSummary 
            selectedOptionPrice={selectedDeliveryOption?.price}
            currentAmount={currentAmount}
          />
        </DialogHeader>
        <div className="space-y-6">
          {!selectedOption && !initialDeliveryMethod ? (
            <PaymentDeliverySelector 
              deliveryOptions={deliveryOptions}
              selectedOption={selectedOption}
              onSelect={(id) => handleOptionSelect(id, deliveryOptions)}
            />
          ) : (
            <>
              <DiscountCodeInput
                discountCode={discountCode}
                onDiscountCodeChange={setDiscountCode}
                onApply={() => applyDiscountCode(undefined, deliveryOptions)}
              />
              <PaymentForm
                clientSecret={clientSecret}
                processing={processing}
                onCancel={() => handleOptionSelect(null, deliveryOptions)}
                onSuccess={handlePaymentSuccess}
                formData={formData}
                selectedDeliveryOption={selectedDeliveryOption?.title}
                amount={currentAmount ? currentAmount * 100 : undefined}
              />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
